import { useEffect, useRef } from 'react';

/**
 * Reads a USB/Bluetooth barcode scanner operating in keyboard-wedge mode.
 *
 * These devices "type" the digits and usually press Enter. That makes them the
 * pragmatic choice for a wall panel: no camera permission, no secure-context
 * requirement, no focus/lighting problems, and it works identically on every
 * browser.
 *
 * A human cannot type 13 digits in under a second, so inter-keystroke timing
 * separates a scan from someone poking at a keyboard.
 */
export interface BarcodeScannerOptions {
  enabled: boolean;
  onScan: (barcode: string) => void;
  /** Max gap between keystrokes still considered part of one scan. */
  maxGapMs?: number;
  minLength?: number;
  /** Some scanners send no terminator; flush after this idle period. */
  flushAfterMs?: number;
}

export function useBarcodeScanner({
  enabled,
  onScan,
  maxGapMs = 60,
  minLength = 6,
  flushAfterMs = 140,
}: BarcodeScannerOptions): void {
  const buffer = useRef('');
  const lastKeyAt = useRef(0);
  const flushTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Keeps the listener stable while still calling the latest handler.
  const handler = useRef(onScan);
  handler.current = onScan;

  useEffect(() => {
    if (!enabled) return;

    const commit = () => {
      const value = buffer.current;
      buffer.current = '';
      clearTimeout(flushTimer.current);
      if (value.length >= minLength) handler.current(value);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      // Never swallow keys meant for a focused field.
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;

      const now = event.timeStamp || performance.now();
      if (now - lastKeyAt.current > maxGapMs) buffer.current = '';
      lastKeyAt.current = now;

      if (event.key === 'Enter') {
        if (buffer.current.length > 0) {
          event.preventDefault();
          commit();
        }
        return;
      }

      if (event.key.length !== 1 || !/[0-9]/.test(event.key)) {
        buffer.current = '';
        return;
      }

      buffer.current += event.key;
      clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(commit, flushAfterMs);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearTimeout(flushTimer.current);
      buffer.current = '';
    };
  }, [enabled, maxGapMs, minLength, flushAfterMs]);
}
